<template>
  <div>
    <el-divider />
    <div class="flex flex-col" style="gap: 12px">
      <div class="row font-medium">
        <p>發票資訊</p>
        <BaseElButton
          v-if="!invoiceRecord && canIssueInvoice"
          type="primary"
          size="small"
          :loading="issueButtonLoading"
          @click="onIssueInvoice"
        >
          開立發票
        </BaseElButton>
        <BaseElButton
          v-else-if="invoiceRecord"
          type="text"
          size="small"
          @click="onViewDetail"
        >
          查看發票詳情
        </BaseElButton>
      </div>

      <BobDisplayForm
        v-if="invoiceRecord"
        :config="displayConfig"
        :fields="displayFields"
      />

      <p v-else class="text-sm text-gray-60">尚未開立發票</p>
    </div>

    <BaseDialog
      v-if="modal.issueInvoice"
      title="開立發票"
      width="610px"
      btn1="開立發票"
      :btn1-loading="issuing"
      :btn1-disabled="isPolling"
      :close-on-click-modal="false"
      @confirm="onSubmitIssueInvoice"
      @close="modal.issueInvoice = false"
    >
      <BobInvoiceForm
        ref="invoiceFormRef"
        source="salesPos"
        type-selector-mode="radioBorder"
        :carrier-types="['0']"
        carrier-type-fixed="0"
        :invoice-types="invoiceTypes"
        :optional-fields="['email', 'companyTitle']"
        :value="invoiceFormValue"
        @update="onInvoiceFormUpdate"
      />
    </BaseDialog>
  </div>
</template>

<script>
import { computed, defineComponent, onBeforeUnmount, reactive, ref, watch } from 'vue'
import { get } from 'lodash'
import { useRouter } from 'vue-router/composables'
import { formatDate } from '@/utils/date'
import { getInvoiceStatusLabel, getInvoiceStatusTagType } from '@/config/invoiceRecord'
import { getInvoiceTypeLabel } from '@/config/invoice'
import { useShop } from '@/use/shop'
import { usePermissions } from '@/use/permissions'
import { useInvoicePolling } from '@/use/useInvoicePolling'
import { apiFormatAdaptor } from '@/utils/api'
import { FindSalesRecord, IssueSalesRecordInvoice } from '@/api/sales'
import {
  buildSalesPOSInvoiceBuyer,
  getSalesPOSInvoiceRecord,
  isSalesPOSInvoiceIssued,
  isSalesPOSInvoicePollingTerminal,
  showSalesPOSInvoiceResultToast,
} from '@/views/SalesPOSCheckout/helpers/invoice'
import {
  showInvoiceIssuedToast,
  showInvoicePrintTimeoutToast,
} from '@/views/Invoice/helpers/printToast'
import BaseElButton from './BaseElButton.vue'
import BaseDialog from '@/components/Dialog/BaseDialog.vue'
import BobDisplayForm from './BobDisplayForm.vue'
import BobInvoiceForm from '@/components/BobInvoiceForm.vue'
import StatusDisplay from '@/components/Tag/StatusDisplay.vue'

export default defineComponent({
  name: 'SalesRecordInvoiceSection',
  components: {
    BaseDialog,
    BaseElButton,
    BobDisplayForm,
    BobInvoiceForm,
  },
  props: {
    salesRecord: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['update'],
  setup (props, { emit }) {
    const router = useRouter()
    const { shopId } = useShop()
    const { checkAction } = usePermissions()
    const { pollInvoiceResult, stopPolling, isPolling } = useInvoicePolling()
    const localSalesRecord = ref(props.salesRecord)
    const invoiceFormRef = ref(null)
    const invoiceFormValue = ref({ type: 'C' })
    const issuing = ref(false)
    const modal = reactive({
      issueInvoice: false,
    })

    const invoiceTypes = ['C', 'carrier', 'B']

    const invoiceRecord = computed(() => {
      return get(localSalesRecord.value, 'InvoiceRecord')
    })

    const salesRecordId = computed(() => get(localSalesRecord.value, 'id'))
    const salesRecordAmount = computed(() => {
      const totalItemsPrice = get(localSalesRecord.value, 'totalItemsPrice')
      const totalDiscountPrice = get(localSalesRecord.value, 'totalDiscountPrice')
      if (totalItemsPrice === undefined || totalItemsPrice === null) return null
      if (totalDiscountPrice === undefined || totalDiscountPrice === null) return null

      return Number(totalItemsPrice) - Number(totalDiscountPrice)
    })
    const isZeroAmountSalesRecord = computed(() => {
      return typeof salesRecordAmount.value === 'number' &&
        !Number.isNaN(salesRecordAmount.value) &&
        salesRecordAmount.value <= 0
    })
    const canIssueInvoice = computed(() => {
      return checkAction('admin.salesRecord.issueInvoice') && !isZeroAmountSalesRecord.value
    })
    const issueButtonLoading = computed(() => issuing.value || isPolling.value)

    const invoiceNo = computed(() => get(invoiceRecord.value, 'invoiceNo') || '-')
    const statusLabel = computed(() => getInvoiceStatusLabel(get(invoiceRecord.value, 'status')))
    const statusTagType = computed(() => getInvoiceStatusTagType(get(invoiceRecord.value, 'status')))
    const issuedAt = computed(() => {
      const at = get(invoiceRecord.value, 'issuedAt')
      return at ? formatDate(at, 'YYYY/MM/DD HH:mm') : '-'
    })
    const buyerTypeLabel = computed(() => {
      return getInvoiceTypeLabel({
        buyerType: get(invoiceRecord.value, 'InvoiceContent.buyer.type'),
        carrierType: get(invoiceRecord.value, 'InvoiceContent.buyer.carrierType'),
      })
    })
    const totalWithTax = computed(() => {
      const total = get(invoiceRecord.value, 'InvoiceContent.total')
      return total ?? '-'
    })

    const displayConfig = {
      rowGap: 12,
      lineHeight: '32px',
    }

    const displayFields = computed(() => [
      {
        label: '發票號碼',
        body: { render: () => invoiceNo.value },
      },
      {
        label: '狀態',
        body: {
          component: StatusDisplay,
          props: {
            tagType: statusTagType.value,
            displayText: statusLabel.value,
            isTag: statusLabel.value !== '-',
          },
        },
      },
      {
        label: '開立時間',
        body: { render: () => issuedAt.value },
      },
      {
        label: '發票類型',
        body: { render: () => buyerTypeLabel.value },
      },
      {
        label: '含稅總額',
        body: {
          component: 'span',
          props: { class: 'font-medium' },
          render: () => `$ ${totalWithTax.value}`,
        },
      },
    ])

    const onViewDetail = () => {
      const invoiceNo = get(invoiceRecord.value, 'invoiceNo')
      if (!invoiceNo) return
      router.push({
        name: 'InvoiceRecords',
        query: {
          invoiceNo,
          sourceType: get(invoiceRecord.value, 'sourceType'),
          view: 'detail',
        },
      })
    }

    const onIssueInvoice = () => {
      if (!salesRecordId.value || !canIssueInvoice.value) return
      modal.issueInvoice = true
    }

    const onInvoiceFormUpdate = (data) => {
      invoiceFormValue.value = data
    }

    const updateSalesRecord = (data) => {
      localSalesRecord.value = data
      emit('update', data)
    }

    const startInvoicePolling = () => {
      let latestInvoiceRecord = null
      let issuedToastShown = false
      const handleInvoicePollingUpdate = (data) => {
        updateSalesRecord(data)
        const record = getSalesPOSInvoiceRecord(data)
        latestInvoiceRecord = record || latestInvoiceRecord
        if (!issuedToastShown && isSalesPOSInvoiceIssued(record)) {
          showInvoiceIssuedToast(record)
          issuedToastShown = true
        }
      }

      pollInvoiceResult({
        pollFn: async () => {
          return await apiFormatAdaptor(FindSalesRecord)({
            shopId: shopId.value,
            id: salesRecordId.value,
          })
        },
        isTerminal: (data) => {
          return isSalesPOSInvoicePollingTerminal(data)
        },
        onUpdate: handleInvoicePollingUpdate,
        onDone: (data) => {
          handleInvoicePollingUpdate(data)
          showSalesPOSInvoiceResultToast({
            record: getSalesPOSInvoiceRecord(data),
            salesRecordId: salesRecordId.value,
            router,
          })
        },
        onTimeout: () => {
          showInvoicePrintTimeoutToast({ record: latestInvoiceRecord, router })
        },
        onError: () => {
          // polling 內部會持續重試，此處不額外處理
        },
      })
    }

    const onSubmitIssueInvoice = async () => {
      if (issuing.value || isPolling.value) return

      const valid = await invoiceFormRef.value?.validateForm()
      if (!valid) return

      issuing.value = true
      const [res, err] = await IssueSalesRecordInvoice({
        shopId: shopId.value,
        id: salesRecordId.value,
        buyer: buildSalesPOSInvoiceBuyer(invoiceFormValue.value),
      })
      issuing.value = false

      if (err) {
        window.$message.error(err)
        return
      }

      modal.issueInvoice = false
      if (res) {
        startInvoicePolling()
      }
    }

    watch(() => props.salesRecord, (data) => {
      localSalesRecord.value = data
    })

    onBeforeUnmount(() => {
      stopPolling()
    })

    return {
      invoiceRecord,
      displayConfig,
      displayFields,
      modal,
      invoiceTypes,
      invoiceFormRef,
      invoiceFormValue,
      issuing,
      isPolling,
      isZeroAmountSalesRecord,
      canIssueInvoice,
      issueButtonLoading,
      onViewDetail,
      onIssueInvoice,
      onInvoiceFormUpdate,
      onSubmitIssueInvoice,
    }
  },
})
</script>

<style lang="postcss" scoped>
.row {
  @apply flex items-center justify-between gap-[8px];
}
</style>
